import { useState } from "react";
import { useEscape } from "../hooks/useEscape.js";
import type { Template } from "@pdfme/common";
import { generateLabel, downloadLabelBlob } from "../api/client.js";

// ─── Field extraction ────────────────────────────────────────────────────────

type SchemaField = {
  name: string;
  type: string;
  options?: unknown;
  [key: string]: unknown;
};

/** Field types that carry no user-supplied content. */
const DECORATIVE_TYPES = new Set(["Line", "Rectangle", "Ellipse"]);

interface FieldDef {
  name: string;
  type: string;
  isTable: boolean;
  isImage: boolean;
  options: string[];
  /** Column headers extracted from the Table schema's `head` property. */
  head: string[];
}

function extractFields(template: Template): FieldDef[] {
  const seen = new Set<string>();
  const fields: FieldDef[] = [];

  for (const page of template.schemas as SchemaField[][]) {
    for (const schema of page) {
      if (
        !schema.name ||
        seen.has(schema.name) ||
        DECORATIVE_TYPES.has(schema.type)
      ) {
        continue;
      }
      seen.add(schema.name);

      const rawOptions = schema.options;
      const options: string[] = Array.isArray(rawOptions)
        ? rawOptions.map(String)
        : [];

      const rawHead = schema.head;
      const head: string[] = Array.isArray(rawHead) ? rawHead.map(String) : [];

      fields.push({
        name: schema.name,
        type: schema.type,
        isTable: schema.type === "Table",
        isImage: schema.type === "Image",
        options,
        head,
      });
    }
  }

  return fields;
}

// ─── Initial values ───────────────────────────────────────────────────────────

function getInitialValue(field: FieldDef): string {
  if (field.isTable) {
    // pdfme table content = data rows only; headers come from schema.head
    const cols =
      field.head.length > 0 ? field.head : ["Column 1", "Column 2", "Column 3"];
    return JSON.stringify(
      [
        cols.map((col) => `${col} value 1`),
        cols.map((col) => `${col} value 2`),
        cols.map((col) => `${col} value 3`),
      ],
      null,
      2,
    );
  }
  switch (field.type) {
    case "QRCode":
      return "https://example.com";
    case "EAN13":
      return "5901234123457";
    case "EAN8":
      return "96385074";
    case "Code39":
    case "Code128":
      return "SAMPLE123";
    case "NW7":
      return "A0123456789A";
    case "ITF14":
      return "12345678901231";
    case "UPCA":
      return "012345678905";
    case "UPCE":
      return "01234565";
    case "GS1DataMatrix":
    case "PDF417":
      return "SAMPLE123";
    case "JapanPost":
      return "1234567-8";
    case "DateTime":
      return new Date().toISOString().slice(0, 16);
    case "Date":
      return new Date().toISOString().split("T")[0] ?? "";
    case "Time":
      return "12:00";
    case "Checkbox":
      return "true";
    case "Select":
    case "RadioGroup":
      return field.options[0] ?? "";
    default:
      return "";
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  templateId: string;
  versionId?: string;
  template: Template;
  onClose: () => void;
}

export function GeneratePdfDialog({
  templateId,
  versionId,
  template,
  onClose,
}: Props) {
  useEscape(onClose);
  const fields = extractFields(template);

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, getInitialValue(f)])),
  );
  const [copies, setCopies] = useState(1);
  const [compressImages, setCompressImages] = useState(true);
  const [imageMaxDimensionPx, setImageMaxDimensionPx] = useState(1400);
  const [imageJpegQuality, setImageJpegQuality] = useState(80);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setValue(name: string, value: string) {
    setValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function buildData(): Record<string, string | string[][]> | null {
    const data: Record<string, string | string[][]> = {};
    const errs: Record<string, string> = {};

    for (const field of fields) {
      const raw = values[field.name] ?? "";
      if (field.isTable) {
        try {
          const parsed: unknown = JSON.parse(raw);
          if (
            !Array.isArray(parsed) ||
            !parsed.every(
              (row) =>
                Array.isArray(row) &&
                row.every((cell) => typeof cell === "string"),
            )
          ) {
            errs[field.name] =
              'Must be a JSON array of string arrays, e.g. [["A","B"],["C","D"]]';
          } else {
            data[field.name] = parsed as string[][];
          }
        } catch {
          errs[field.name] = "Invalid JSON — must be an array of arrays";
        }
      } else {
        data[field.name] = raw;
      }
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return null;
    }

    return data;
  }

  async function handleGenerate() {
    const data = buildData();
    if (!data) return;

    setError(null);
    setGenerating(true);

    try {
      const result = await generateLabel({
        templateId,
        versionId,
        data: [data],
        options: {
          copies,
          compressImages,
          imageMaxDimensionPx: compressImages ? imageMaxDimensionPx : undefined,
          imageJpegQuality: compressImages ? imageJpegQuality : undefined,
        },
      });

      const blob = await downloadLabelBlob(result.downloadUrl);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="dialog-overlay">
      <div className="dialog dialog--wide">
        <h3>Generate PDF</h3>
        <p className="dialog__subtitle">
          Fill in the field values and click Generate to download the PDF.
        </p>

        {fields.length === 0 ? (
          <p className="generate-dialog__empty">
            This template has no input fields yet. Add some fields in the
            designer first.
          </p>
        ) : (
          <div className="generate-dialog__fields">
            {fields.map((field) => (
              <div key={field.name} className="form-group">
                <label>
                  {field.name}
                  <span className="generate-dialog__type-badge">
                    {field.type}
                  </span>
                </label>

                {field.isTable ? (
                  <textarea
                    className="generate-dialog__textarea"
                    value={values[field.name] ?? ""}
                    onChange={(e) => setValue(field.name, e.target.value)}
                    rows={6}
                    spellCheck={false}
                  />
                ) : field.options.length > 0 ? (
                  <select
                    value={values[field.name] ?? ""}
                    onChange={(e) => setValue(field.name, e.target.value)}
                  >
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : field.type === "Checkbox" ? (
                  <select
                    value={values[field.name] ?? "true"}
                    onChange={(e) => setValue(field.name, e.target.value)}
                  >
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={values[field.name] ?? ""}
                    onChange={(e) => setValue(field.name, e.target.value)}
                    placeholder={
                      field.isImage
                        ? "https://example.com/image.png or data:image/png;base64,..."
                        : `Enter ${field.name}`
                    }
                  />
                )}

                {fieldErrors[field.name] && (
                  <span className="form-error">{fieldErrors[field.name]}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="generate-dialog__copies">
          <label htmlFor="generate-copies">Copies</label>
          <input
            id="generate-copies"
            type="number"
            min={1}
            max={100}
            value={copies}
            onChange={(e) =>
              setCopies(
                Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)),
              )
            }
          />
        </div>

        <div className="generate-dialog__compression">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={compressImages}
                onChange={(e) => setCompressImages(e.target.checked)}
              />
              Compress images
            </label>
            <p className="form-help">
              Reduce PDF file size by optimizing images. Recommended for most labels.
            </p>
          </div>

          {compressImages && (
            <>
              <div className="form-group">
                <label htmlFor="image-max-dimension">Max image dimension (px)</label>
                <input
                  id="image-max-dimension"
                  type="number"
                  min={100}
                  max={5000}
                  value={imageMaxDimensionPx}
                  onChange={(e) =>
                    setImageMaxDimensionPx(
                      Math.max(100, Math.min(5000, parseInt(e.target.value, 10) || 1400)),
                    )
                  }
                />
                <p className="form-help">
                  Images larger than this will be resized down. Default: 1400px
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="image-jpeg-quality">JPEG quality (%)</label>
                <input
                  id="image-jpeg-quality"
                  type="number"
                  min={1}
                  max={100}
                  value={imageJpegQuality}
                  onChange={(e) =>
                    setImageJpegQuality(
                      Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 80)),
                    )
                  }
                />
                <p className="form-help">
                  Lower values reduce file size but may decrease image quality. Default: 80%
                </p>
              </div>
            </>
          )}
        </div>

        {error && <p className="generate-dialog__error">{error}</p>}

        <div className="dialog__actions">
          <button className="btn" onClick={onClose} disabled={generating}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void handleGenerate()}
            disabled={generating || fields.length === 0}
          >
            {generating ? "Generating…" : "Generate & Download"}
          </button>
        </div>
      </div>
    </div>
  );
}
